import { useState } from 'react';
import { Popover } from 'radix-ui';
import { CheckIcon, Share2Icon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CalendarQuery, SavedView, SortOrder } from '@complan/contracts';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { savedViewToView } from '@/shared/url/calendar-search-params';
import {
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
  useUpdateSavedView,
} from './saved-view-data';

/**
 * The Saved_View menu (task 22.6; R11.10-R11.16).
 *
 * It hangs off the filter bar's `Save view` control and gathers every Saved_View
 * operation in one panel:
 *
 * - **create** — name and store the *current* filter/orientation/sort (R11.10);
 * - **open** — apply a stored view by writing its year and search to the URL
 *   (R11.11), which is what makes the applied state the single source of truth;
 * - **rename / share / unpublish / delete** — the write operations, offered only
 *   on views the requester may modify (`editableByRequester`, R11.14). Share and
 *   unpublish are the `isShared` toggle; rename is the `name` edit — both are one
 *   `PUT`.
 *
 * The stale-reference indications of R11.15/R11.16 (task 17.7) are surfaced per
 * view straight from the API-resolved facts: `deactivatedFilterValues` names each
 * deactivated value still in use, and `omittedFilterValueCount` states how many
 * no-longer-existing values were dropped. Nothing here recomputes them — the API
 * already partitioned the stored references.
 */

export interface SavedViewMenuProps {
  readonly query: CalendarQuery;
  readonly sort: SortOrder;
  readonly onApplySavedView: (year: number, search: URLSearchParams) => void;
}

export function SavedViewMenu({
  query,
  sort,
  onApplySavedView,
}: SavedViewMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const viewsQuery = useSavedViews('calendar');
  const createView = useCreateSavedView();
  const updateView = useUpdateSavedView();
  const deleteView = useDeleteSavedView();

  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  const list = viewsQuery.data;
  const atLimit = list ? list.ownedCount >= list.maxOwnedPerUser : false;

  const create = (): void => {
    const name = newName.trim();
    if (name.length === 0) return;
    createView.mutate(
      {
        name,
        filters: stripOrientation(query),
        orientation: query.orientation,
        sort,
        isShared: false,
        // R6.10: the screen this view belongs to, so it is offered here and not on a
        // dashboard that cannot render a calendar orientation.
        screen: 'calendar',
      },
      {
        onSuccess: () => {
          setNewName('');
        },
      },
    );
  };

  const applyAndClose = (view: SavedView): void => {
    const { year, search } = savedViewToView(view);
    onApplySavedView(year, search);
    setOpen(false);
  };

  const commitRename = (view: SavedView): void => {
    const name = renameDraft.trim();
    if (name.length === 0) {
      setRenamingId(null);
      return;
    }
    updateView.mutate(
      { id: view.id, request: writeFrom(view, { name }) },
      { onSuccess: () => setRenamingId(null) },
    );
  };

  const toggleShare = (view: SavedView): void => {
    updateView.mutate({ id: view.id, request: writeFrom(view, { isShared: !view.isShared }) });
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="outline" size="sm">
          {t('savedView.open')}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="border-line bg-panel z-50 w-[320px] rounded-[var(--radius-card)] border p-3 shadow-[var(--shadow-drawer)]"
        >
          <div className="flex flex-col gap-3">
            <section className="flex flex-col gap-1">
              <label
                htmlFor="saved-view-name"
                className="text-label text-[10.5px] font-semibold uppercase tracking-wide"
              >
                {t('savedView.saveCurrent')}
              </label>
              <div className="flex gap-2">
                <input
                  id="saved-view-name"
                  type="text"
                  value={newName}
                  maxLength={100}
                  placeholder={t('savedView.namePlaceholder')}
                  disabled={atLimit}
                  onChange={(event) => setNewName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      create();
                    }
                  }}
                  className="border-line bg-panel text-txt min-w-0 flex-1 rounded-[var(--radius-control)] border px-[7px] py-[5px] text-[12px] outline-none focus-visible:border-accent-line"
                />
                <Button
                  size="sm"
                  onClick={create}
                  disabled={atLimit || newName.trim().length === 0 || createView.isPending}
                >
                  {t('common.save')}
                </Button>
              </div>
              {atLimit ? (
                <p className="text-[10.5px] text-[var(--a-gov-txt)]">
                  {t('savedView.limitReached', { max: list?.maxOwnedPerUser })}
                </p>
              ) : null}
            </section>

            <section className="flex flex-col gap-1">
              <p className="text-label text-[10.5px] font-semibold uppercase tracking-wide">
                {t('savedView.heading')}
              </p>
              {viewsQuery.isLoading ? (
                <p className="text-muted-foreground text-[12px]" role="status">
                  {t('common.loading')}
                </p>
              ) : null}
              {viewsQuery.isError ? (
                <p className="text-[12px] text-[var(--a-hard-txt)]">{t('savedView.loadError')}</p>
              ) : null}
              {list && list.items.length === 0 ? (
                <p className="text-muted-foreground text-[12px]">{t('savedView.none')}</p>
              ) : null}
              <ul className="flex max-h-[260px] flex-col gap-1 overflow-y-auto">
                {list?.items.map((view) => (
                  <li
                    key={view.id}
                    className="border-line-soft flex flex-col gap-1 rounded-[var(--radius-control)] border p-2"
                  >
                    <div className="flex items-center gap-2">
                      {renamingId === view.id ? (
                        <input
                          type="text"
                          value={renameDraft}
                          maxLength={100}
                          autoFocus
                          onChange={(event) => setRenameDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              commitRename(view);
                            }
                            if (event.key === 'Escape') setRenamingId(null);
                          }}
                          onBlur={() => commitRename(view)}
                          className="border-line bg-panel text-txt min-w-0 flex-1 rounded-[var(--radius-control)] border px-[6px] py-[3px] text-[12px] outline-none focus-visible:border-accent-line"
                        />
                      ) : (
                        <button
                          type="button"
                          className="text-txt hover:text-accent-hover min-w-0 flex-1 truncate text-left text-[12.5px] font-medium"
                          onClick={() => applyAndClose(view)}
                          title={view.name}
                        >
                          {view.name}
                        </button>
                      )}
                      {view.isShared ? (
                        <span
                          className="text-accent-hover bg-accent-soft inline-flex items-center gap-1 rounded-[var(--radius-badge)] px-[5px] py-[1px] text-[10px] font-semibold"
                          title={t('savedView.sharedTitle')}
                        >
                          <Share2Icon className="size-3" aria-hidden="true" /> {t('savedView.shared')}
                        </span>
                      ) : null}
                    </div>

                    <StaleReferenceIndication view={view} />

                    {view.editableByRequester ? (
                      <div className="flex items-center gap-2 pt-1">
                        <MenuAction
                          onClick={() => {
                            setRenamingId(view.id);
                            setRenameDraft(view.name);
                          }}
                        >
                          {t('savedView.rename')}
                        </MenuAction>
                        <MenuAction onClick={() => toggleShare(view)}>
                          {view.isShared ? t('savedView.unpublish') : t('savedView.share')}
                        </MenuAction>
                        <MenuAction
                          tone="danger"
                          onClick={() => deleteView.mutate(view.id)}
                          icon={<Trash2Icon className="size-3" aria-hidden="true" />}
                        >
                          {t('savedView.delete')}
                        </MenuAction>
                      </div>
                    ) : (
                      <p className="text-muted-foreground inline-flex items-center gap-1 pt-1 text-[10.5px]">
                        <CheckIcon className="size-3" aria-hidden="true" /> {t('savedView.sharedViewOnly')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The R11.15/R11.16 indication: names each deactivated value in use and counts dropped ones. */
function StaleReferenceIndication({ view }: { view: SavedView }): React.JSX.Element | null {
  const { t } = useTranslation();
  const hasDeactivated = view.deactivatedFilterValues.length > 0;
  const hasOmitted = view.omittedFilterValueCount > 0;
  if (!hasDeactivated && !hasOmitted) {
    return null;
  }
  return (
    <div className="rounded-[var(--radius-control)] bg-[var(--a-warn-bg)] px-[6px] py-[4px] text-[10.5px] text-[var(--a-warn-txt)]">
      {hasDeactivated ? (
        <p>
          {t('savedView.deactivatedValues', {
            names: view.deactivatedFilterValues.map((value) => value.name).join(', '),
          })}
        </p>
      ) : null}
      {hasOmitted ? (
        <p>{t('savedView.omittedValues', { count: view.omittedFilterValueCount })}</p>
      ) : null}
    </div>
  );
}

function MenuAction({
  children,
  onClick,
  tone = 'default',
  icon,
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
  icon?: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-[11px] font-medium',
        tone === 'danger' ? 'text-[var(--risk)] hover:underline' : 'text-accent-hover hover:underline',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Drop the orientation off a calendar query, leaving the pure filter grammar the API stores. */
function stripOrientation(query: CalendarQuery): SavedView['filters'] {
  const filters: Record<string, unknown> = { ...query };
  delete filters['orientation'];
  return filters as SavedView['filters'];
}

/**
 * A whole-write payload for a `PUT`, since the API replaces the stored view: start
 * from the view's current stored parts and override the one field that changed.
 */
function writeFrom(
  view: SavedView,
  change: { name?: string; isShared?: boolean },
): {
  name: string;
  filters: SavedView['filters'];
  orientation: SavedView['orientation'];
  sort: SortOrder;
  isShared: boolean;
  screen: SavedView['screen'];
} {
  return {
    name: change.name ?? view.name,
    filters: view.filters,
    orientation: view.orientation,
    sort: view.sort,
    // Preserved rather than re-stated: a rename or a share toggle must not migrate a
    // view to another screen.
    screen: view.screen,
    isShared: change.isShared ?? view.isShared,
  };
}
